import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  Typography,
  Stack,
} from '@mui/material';
import { ChevronDownIcon, SeatIcon } from '@tix-ticketing/ui-icons/outline';
import { FormattedMessage } from 'react-intl';
import { TicketCard } from '../cards/ticket-card';
import { useIsMobile } from '../hooks/use-is-mobile';
import type { CurrencyData, TicketData } from '../types';
import { IconOnlyButton } from './button/icon-only-button';
import type { TicketTypeOption } from './ticket-type-selector';

export const EventTicketsAccordion = ({
  tickets,
  ticketTypeOptions,
  defaultExpanded,
  showCustomerInfo = false,
  rightContent,
  currencyData,
  inAside = false,
  editable,
  disabled = false,
  onRemoveTicket,
  onChangeTicketType,
  onShow360Ticket,
  onViewSeats,
  selectedTicketIds,
  onToggleTicket,
  ticketSelectLabel,
  renderDiscountClubMark,
}: {
  tickets: TicketData[];
  ticketTypeOptions?: TicketTypeOption[];
  defaultExpanded?: boolean;
  showCustomerInfo?: boolean;
  rightContent?: React.ReactNode;
  currencyData?: CurrencyData;
  inAside?: boolean;
  /** Whether tickets can be edited (remove/change type). Defaults to `!inAside`. */
  editable?: boolean;
  /**
   * Renders the tickets as read-only: every label uses the disabled text
   * colour, editing is off and the seat button is inactive. The list can still
   * be expanded and collapsed.
   */
  disabled?: boolean;
  onRemoveTicket?: (ticket: TicketData) => void;
  onChangeTicketType?: (
    encryptedTicketId: string,
    ticketTypeId: number
  ) => void;
  onShow360Ticket?: (ticket: TicketData) => void;
  onViewSeats?: () => void;
  /**
   * Encrypted ids of the tickets currently ticked. Together with
   * `onToggleTicket` this renders a leading checkbox on every ticket; without
   * `onToggleTicket` the tickets have no checkbox.
   */
  selectedTicketIds?: Set<string>;
  onToggleTicket?: (ticket: TicketData) => void;
  /** Accessible name for a ticket's checkbox — the app owns the wording. */
  ticketSelectLabel?: string;
  /** Renders the logo of a ticket's discount club, looked up by its name. */
  renderDiscountClubMark?: (clubName?: string | null) => React.ReactNode;
}) => {
  const isMobile = useIsMobile() || inAside;
  const isEditable = !disabled && (editable ?? !inAside);

  return (
    <>
      <Accordion
        defaultExpanded={defaultExpanded}
        disableGutters
        sx={{
          boxShadow: 'unset',
          background: 'transparent',
          '&:before': {
            display: 'none', // removes the default divider line
          },
          // Labels inside the tickets set their own colours, so the disabled
          // state hands every one of them the accordion's colour.
          ...(disabled && {
            color: 'text.disabled',
            '& .MuiTypography-root': { color: 'inherit' },
          }),
        }}
      >
        {/* The actions sit next to the summary rather than inside it: the
            summary renders as a <button>, which cannot contain other buttons. */}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: 'center',
          }}
        >
          <AccordionSummary
            sx={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              p: 0,
              fontWeight: 500,
              flexDirection: 'row-reverse',
              '& .MuiAccordionSummary-content': {
                my: 0,
              },
            }}
            expandIcon={
              <IconOnlyButton
                component="span"
                variant="contained"
                color="monochrome"
                size="small"
                sx={{
                  color: 'action.active',
                  '&:hover': {
                    color: 'action.active',
                  },
                }}
              >
                <ChevronDownIcon />
              </IconOnlyButton>
            }
            aria-controls="panel1-content"
            id="panel1-header"
          >
            <Typography
              variant="body1Bold"
              sx={{
                ml: 1.5,
              }}
            >
              <FormattedMessage
                id="common.xTickets"
                defaultMessage="{tickets, plural, one {# ticket} other {# tickets}}"
                values={{ tickets: tickets.length }}
              />
            </Typography>
          </AccordionSummary>
          {onViewSeats && (
            <Button
              color="monochrome"
              variant="outlined"
              size={isMobile ? 'small' : 'medium'}
              disabled={disabled}
              endIcon={<SeatIcon />}
              onClick={onViewSeats}
            >
              {isMobile ? (
                <FormattedMessage id="common.seats" defaultMessage="Seats" />
              ) : (
                <FormattedMessage
                  id="cart.viewSeats"
                  defaultMessage="View seats"
                />
              )}
            </Button>
          )}
          {rightContent}
        </Stack>
        <AccordionDetails
          sx={{
            p: 0,
            pt: { xs: 1.5, sm: inAside ? 1.5 : 2 },
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          {tickets.map((ticket) => (
            <TicketCard
              key={ticket.encryptedId}
              ticket={ticket}
              seatNotice={ticket.seatNotice ?? undefined}
              editable={isEditable}
              showCustomerInfo={showCustomerInfo}
              isMobile={isMobile}
              onRemoveTicket={
                onRemoveTicket ? () => onRemoveTicket(ticket) : undefined
              }
              currencyData={currencyData}
              onShow360View={() => onShow360Ticket && onShow360Ticket(ticket)}
              ticketTypeOptions={ticketTypeOptions}
              onChangeTicketType={onChangeTicketType}
              onToggleSelect={
                onToggleTicket ? () => onToggleTicket(ticket) : undefined
              }
              selected={
                !!ticket.encryptedId &&
                !!selectedTicketIds?.has(ticket.encryptedId)
              }
              selectLabel={ticketSelectLabel}
              discountClubMark={
                ticket.discountClub
                  ? renderDiscountClubMark?.(ticket.discountClub.name)
                  : undefined
              }
            />
          ))}
        </AccordionDetails>
      </Accordion>
    </>
  );
};
